import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { ApolloProvider } from "@apollo/client/react";
import { prerenderStatic } from "@apollo/client/react/ssr";
import { CSSReset, theme, ThemeProvider } from "@govlaunch/web";
import { IncomingHttpHeaders } from "http";
import type { AppContext, AppInitialProps, AppProps } from "next/app";
import Head from "next/head";
import { renderToStaticMarkup } from "react-dom/server";
import IntlWrapper from "../components/IntlWrapper";
import MobileDetectContext, { getInitialPropsFromUserAgent } from "../components/MobileDetectContext";
import makeApolloClient from "../lib/makeApolloClient";
import "../public/static/style.css";

interface SharingPageAppProps {
  apolloState?: NormalizedCacheObject;
  apolloClient?: ApolloClient;
  userAgent?: string;
  headers?: IncomingHttpHeaders;
}

function SharingPageApp({
  Component,
  pageProps,
  userAgent,
  apolloState,
  apolloClient
}: AppProps & SharingPageAppProps) {
  return (
    <>
      <Head>
        <title>Govlaunch</title>
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      </Head>

      <ApolloProvider client={apolloClient ?? makeApolloClient(apolloState)}>
        <MobileDetectContext.Provider value={getInitialPropsFromUserAgent(userAgent || "")}>
          <ThemeProvider theme={theme}>
            <CSSReset />
            <IntlWrapper>
              <Component {...pageProps} />
            </IntlWrapper>
          </ThemeProvider>
        </MobileDetectContext.Provider>
      </ApolloProvider>
    </>
  );
}

export default SharingPageApp;

SharingPageApp.getInitialProps = async (pageContext: AppContext): Promise<SharingPageAppProps & AppInitialProps> => {
  const { ctx } = pageContext;
  const userAgent = pageContext.ctx.req ? pageContext.ctx.req.headers["user-agent"] : navigator.userAgent;
  const apolloClient = makeApolloClient();

  let pageProps = {};
  const { AppTree, Component } = pageContext;

  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps(ctx);
  }

  if (typeof window === "undefined") {
    if (ctx.res && ctx.res.writableEnded) {
      return {
        pageProps,
        userAgent
      };
    }

    try {
      await prerenderStatic({
        context: {
          client: apolloClient
        },
        tree: <AppTree Component={Component} pageProps={pageProps} apolloClient={apolloClient} />,
        renderFunction: renderToStaticMarkup,
        ignoreResults: true
      });
    } catch (error) {
      console.error(error);
    }
  }

  const apolloState = apolloClient.extract() as NormalizedCacheObject;
  // @ts-expect-error
  apolloClient.toJSON = () => null;

  return {
    pageProps,
    apolloState,
    apolloClient,
    userAgent
  };
};
